import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { creatorListingsPath, productCreatorStagePath } from "@travily/routes";
import type ListingsCreatorContent from "@travily/types/pages/ListingsCreatorContent";
import { useState } from "react";
import { useDirectUpload } from "@/hooks/use-direct-upload";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useWizardForm } from "@/lib/wizard/use-wizard-form";
import { ProductContentStepView } from "./show.view";

type PageProps = ListingsCreatorContent;

type FormData = {
  delivery_kind: "file" | "link";
  delivery_url: string;
  signed_id: string;
  // True once the user clears the existing server-side file without picking a new one — tells the save
  // op to drop it rather than silently keep the prior attachment. Reset whenever a fresh file is picked.
  remove_file: boolean;
};

const ProductContentStep = ({
  listing_id,
  stage_index,
  stage_order,
  delivery_kind,
  delivery_url,
  delivery_file_name,
}: PageProps) => {
  const initialKind = (delivery_kind ?? "file") as "file" | "link";

  const { form, fieldError, currentStage, totalStages, onBack, onPrimary, primaryPending } = useWizardForm<FormData>({
    routes: {
      stageUrl: (stage) => productCreatorStagePath(listing_id, stage),
      entryUrl: creatorListingsPath(),
    },
    stageName: "content",
    stageIndex: stage_index,
    stageOrder: stage_order,
    initialData: {
      delivery_kind: initialKind,
      delivery_url: delivery_url ?? "",
      signed_id: "",
      remove_file: false,
    },
  });

  const { upload, isUploading, progress } = useDirectUpload();
  const [picked, setPicked] = useState<{ name: string; size: number } | null>(null);

  const hasExistingFile = delivery_kind === "file" && delivery_file_name != null;
  const fileError = fieldError("signed_id") ?? fieldError("delivery_kind");
  const linkError = fieldError("delivery_url") ?? fieldError("delivery_kind");

  const handleFileSelect = async (file: File) => {
    setPicked({ name: file.name, size: file.size });
    form.setData("remove_file", false);
    const signedId = await upload(file);
    form.setData("signed_id", signedId);
  };

  const clearFile = () => {
    setPicked(null);
    form.setData("signed_id", "");
    form.setData("remove_file", true);
  };

  const setKind = (value: string) => {
    // Switching kind starts that mode fresh: drop the picked-file state and the remove flag so a
    // file→link→file round-trip can't leave a phantom card (picked name with no signed_id) or a stale
    // remove_file that blocks saving the still-attached server file. Returning to "file" re-surfaces
    // the original attachment cleanly.
    setPicked(null);
    if (value === "link") {
      form.setData({ ...form.data, delivery_kind: "link", signed_id: "", remove_file: false });
    } else {
      form.setData({ ...form.data, delivery_kind: "file", delivery_url: "", remove_file: false });
    }
  };

  const fileName =
    picked?.name ?? (hasExistingFile && !form.data.remove_file ? (delivery_file_name ?? undefined) : undefined);

  const topBarActions = useCreatorTopBarActions({ saveHref: creatorListingsPath() });

  return (
    <>
      <Head title={t("web.products.creator.steps.content.show.meta_title")}>
        <meta name="description" content={t("web.products.creator.steps.content.show.meta_description")} />
      </Head>
      <ProductContentStepView
        currentStage={currentStage}
        totalStages={totalStages}
        onBack={onBack}
        topBarActions={topBarActions}
        onPrimary={onPrimary}
        primaryPending={primaryPending}
        deliveryKind={form.data.delivery_kind}
        onKindChange={setKind}
        fileName={fileName}
        fileSize={picked?.size}
        fileError={fileError}
        isUploading={isUploading}
        progress={progress}
        onFileSelect={handleFileSelect}
        onFileRemove={clearFile}
        deliveryUrl={form.data.delivery_url}
        linkError={linkError}
        onDeliveryUrlChange={(value) => form.setData("delivery_url", value)}
      />
    </>
  );
};

export default ProductContentStep;
